import { OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Component, EventEmitter, Input, Output } from '@angular/core';

import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { tinykeys } from 'tinykeys';

import { SHARED_MODULES } from '../../../shared.';
import { HotkeyTooltipComponent } from '../hotkey-tooltip/hotkey-tooltip.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhTooltipDirective } from '../../directives/ph-tooltip.directive';

export interface ZoomParams {
	min: number,
	max: number,
	step: number,
	value: number
}

const defaultZoom: ZoomParams = {
	min: 20,
	max: 300,
	step: 20,
	value: 100
}

@Component({
    selector: 'ph-simple-zoom',
    templateUrl: './ph-simple-zoom.component.html',
    styleUrls: ['./ph-simple-zoom.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [SHARED_MODULES, HotkeyTooltipComponent, PhIconWrapperComponent, PhTooltipDirective],
	standalone: true
})
export class PhSimpleZoomComponent implements OnInit, OnDestroy {

    zoom: FormControl<number> = new FormControl<number>(defaultZoom.value);

	@Input() set target(el: HTMLElement | null) {
		this._targetElement = el;
	}

    @Input() min: number = defaultZoom.min;
    @Input() max: number = defaultZoom.max;
    @Input() step: number = defaultZoom.step;
    @Input() disabled: boolean = false;
    @Input() available: boolean = true;

	@Input('zoom') set zoomValue(value: number) {
		if (this.zoom.value !== value) this.zoom.setValue(value);
	}

	@Output('apply') changeEmitter: EventEmitter<number> = new EventEmitter<number>();

    private _targetElement: HTMLElement = null;
    private tinyKeysSubscription: () => void;

    constructor() {}

	ngOnInit(): void {
		this.zoom.valueChanges
			.pipe(debounceTime(500))
			.subscribe((value: number): void => {
				this.changeEmitter.emit(value)
			});

		this.tinyKeysSubscription = tinykeys(this._targetElement || window, {
			'$mod+Equal': (event: KeyboardEvent): void => {
				if (this.available) {
					event.preventDefault();
					this.zoomIncrease();
				}
			},
            '$mod+NumpadAdd': (event: KeyboardEvent): void => {
				if (this.available) {
					event.preventDefault();
					this.zoomIncrease();
				}
			},
            '$mod+Minus': (event: KeyboardEvent): void => {
				if (this.available) {
					event.preventDefault();
					this.zoomDecrease();
				}
			},
			'$mod+NumpadSubtract': (event: KeyboardEvent): void => {
				if (this.available) {
					event.preventDefault();
					this.zoomDecrease();
				}
			}
		});
    }

	zoomDecrease(): void {
		if (this.disabled) return;
		const value: number = Math.max(this.zoom.value - this.step, this.min);
		if (value !== this.zoom.value) this.zoom.setValue(value);
	}

	zoomIncrease(): void {
		if (this.disabled) return;
		const value: number = Math.min(this.zoom.value + this.step, this.max);
		if (value !== this.zoom.value) this.zoom.setValue(value);
	}

	ngOnDestroy(): void {
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();
	}
}
